/**
 * 筛选工作台状态 Slice
 * 交易所筛选审核管理
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';
import { QuestionnaireType, QuestionnaireStatus, RiskLevel } from './questionnaireSlice';

// 筛选状态枚举
export enum ScreeningStatus {
  PENDING = 'pending',
  SCREENING = 'screening',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  REVISION_REQUIRED = 'revision_required',
}

// 筛选项目接口
export interface ScreeningProject {
  id: string;
  questionnaireId: string;
  projectName: string;
  issuerName: string;
  industry: string;
  issuanceAmount: number;
  bondType: string;
  
  // 筛选信息
  status: ScreeningStatus;
  aiScore: number;
  riskLevel: RiskLevel;
  
  // 审核信息
  reviewedBy?: string;
  reviewedAt?: string;
  reviewComments?: string;
  
  // 关键指标
  keyMetrics: {
    financialHealth: number;
    environmentalImpact: number;
    socialImpact: number;
    governanceScore: number;
    complianceScore: number;
  };
  
  // 时间信息
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
}

// 筛选统计接口
export interface ScreeningStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  averageScore: number;
  processingTime: number; // 平均处理时间（小时）
}

// 筛选筛选器接口
export interface ScreeningFilters {
  status: ScreeningStatus[];
  riskLevel: RiskLevel[];
  industry: string[];
  scoreRange: [number, number];
  dateRange: [string, string] | null;
  search: string;
}

// 筛选状态接口
export interface ScreeningState {
  // 筛选项目列表
  projects: ScreeningProject[];
  
  // 当前项目
  currentProject: ScreeningProject | null;
  
  // 筛选器
  filters: ScreeningFilters;
  
  // 分页
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  
  // 排序
  sorting: {
    field: keyof ScreeningProject;
    order: 'asc' | 'desc';
  };
  
  // 选中项目
  selectedProjects: string[];
  
  // 状态标识
  isLoading: boolean;
  isBatchProcessing: boolean;
  
  // 错误信息
  error: string | null;
  
  // 统计信息
  stats: ScreeningStats;
  
  // 视图模式
  viewMode: 'table' | 'cards';
  
  // 自动刷新
  autoRefresh: boolean;
  refreshInterval: number; // 秒
}

// 初始状态
const initialState: ScreeningState = {
  projects: [],
  currentProject: null,
  filters: {
    status: [],
    riskLevel: [],
    industry: [],
    scoreRange: [0, 100],
    dateRange: null,
    search: '',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
  sorting: {
    field: 'submittedAt',
    order: 'desc',
  },
  selectedProjects: [],
  isLoading: false,
  isBatchProcessing: false,
  error: null,
  stats: {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    averageScore: 0,
    processingTime: 0,
  },
  viewMode: 'table',
  autoRefresh: false,
  refreshInterval: 30,
};

// 筛选slice
const screeningSlice = createSlice({
  name: 'screening',
  initialState,
  reducers: {
    // 设置加载状态
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // 设置批量处理状态
    setBatchProcessing: (state, action: PayloadAction<boolean>) => {
      state.isBatchProcessing = action.payload;
    },
    
    // 设置错误
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // 清除错误
    clearError: (state) => {
      state.error = null;
    },
    
    // 设置项目列表
    setProjects: (state, action: PayloadAction<ScreeningProject[]>) => {
      state.projects = action.payload;
    },
    
    // 设置当前项目
    setCurrentProject: (state, action: PayloadAction<ScreeningProject | null>) => {
      state.currentProject = action.payload;
    },
    
    // 更新项目
    updateProject: (state, action: PayloadAction<{ id: string; updates: Partial<ScreeningProject> }>) => {
      const { id, updates } = action.payload;
      const index = state.projects.findIndex(p => p.id === id);
      if (index !== -1) {
        state.projects[index] = { ...state.projects[index], ...updates };
      }
      
      if (state.currentProject?.id === id) {
        state.currentProject = { ...state.currentProject, ...updates };
      }
    },
    
    // 批量更新项目状态
    batchUpdateProjects: (state, action: PayloadAction<{ ids: string[]; updates: Partial<ScreeningProject> }>) => {
      const { ids, updates } = action.payload;
      state.projects.forEach(project => {
        if (ids.includes(project.id)) {
          Object.assign(project, updates);
        }
      });
    },
    
    // 设置筛选器
    setFilters: (state, action: PayloadAction<Partial<ScreeningFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // 清除筛选器
    clearFilters: (state) => {
      state.filters = {
        status: [],
        riskLevel: [],
        industry: [],
        scoreRange: [0, 100],
        dateRange: null,
        search: '',
      };
    },
    
    // 设置分页
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // 设置排序
    setSorting: (state, action: PayloadAction<typeof initialState.sorting>) => {
      state.sorting = action.payload;
    },
    
    // 选择项目
    selectProject: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.selectedProjects.includes(id)) {
        state.selectedProjects.push(id);
      }
    },
    
    // 取消选择项目
    deselectProject: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedProjects = state.selectedProjects.filter(pid => pid !== id);
    },
    
    // 全选/取消全选
    toggleSelectAll: (state) => {
      if (state.selectedProjects.length === state.projects.length) {
        state.selectedProjects = [];
      } else {
        state.selectedProjects = state.projects.map(p => p.id);
      }
    },
    
    // 清除选择
    clearSelection: (state) => {
      state.selectedProjects = [];
    },
    
    // 设置统计信息
    setStats: (state, action: PayloadAction<ScreeningStats>) => {
      state.stats = action.payload;
    },
    
    // 设置视图模式
    setViewMode: (state, action: PayloadAction<'table' | 'cards'>) => {
      state.viewMode = action.payload;
    },
    
    // 设置自动刷新
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload;
    },
    
    // 设置刷新间隔
    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload;
    },
  },
});

// 扩展API slice - 筛选相关端点
export const screeningApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // 获取筛选项目列表
    getScreeningProjects: builder.query<GetScreeningProjectsResponse, GetScreeningProjectsRequest>({
      query: (params) => ({
        url: '/screening/projects',
        params,
      }),
      providesTags: ['Screening'],
    }),
    
    // 获取单个筛选项目
    getScreeningProject: builder.query<GetScreeningProjectResponse, string>({
      query: (id) => `/screening/projects/${id}`,
      providesTags: (result, error, id) => [{ type: 'Screening', id }],
    }),
    
    // 审核项目
    reviewProject: builder.mutation<ReviewProjectResponse, ReviewProjectRequest>({
      query: ({ id, ...data }) => ({
        url: `/screening/projects/${id}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Screening', id }],
      async onQueryStarted({ id, status, comments }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            dispatch(screeningSlice.actions.updateProject({
              id,
              updates: {
                status: status as ScreeningStatus,
                reviewComments: comments,
                reviewedAt: new Date().toISOString(),
              }
            }));
            dispatch(screeningSlice.actions.clearError());
          }
        } catch (error: any) {
          dispatch(screeningSlice.actions.setError(
            error.error?.data?.message || '审核失败'
          ));
        }
      },
    }),
    
    // 批量审核
    batchReview: builder.mutation<BatchReviewResponse, BatchReviewRequest>({
      query: (data) => ({
        url: '/screening/projects/batch-review',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Screening'],
      async onQueryStarted({ projectIds, status, comments }, { dispatch, queryFulfilled }) {
        dispatch(screeningSlice.actions.setBatchProcessing(true));
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            dispatch(screeningSlice.actions.batchUpdateProjects({
              ids: projectIds,
              updates: {
                status: status as ScreeningStatus,
                reviewComments: comments,
                reviewedAt: new Date().toISOString(),
              }
            }));
            dispatch(screeningSlice.actions.clearSelection());
            dispatch(screeningSlice.actions.setBatchProcessing(false));
            dispatch(screeningSlice.actions.clearError());
          }
        } catch (error: any) {
          dispatch(screeningSlice.actions.setError(
            error.error?.data?.message || '批量审核失败'
          ));
          dispatch(screeningSlice.actions.setBatchProcessing(false));
        }
      },
    }),
    
    // 获取筛选统计
    getScreeningStats: builder.query<ScreeningStatsResponse, void>({
      query: () => '/screening/stats',
      providesTags: ['Screening'],
    }),
    
    // 导出筛选数据
    exportScreeningData: builder.mutation<ExportDataResponse, ExportDataRequest>({
      query: (params) => ({
        url: '/screening/export',
        method: 'POST',
        body: params,
      }),
    }),
  }),
});

// 导出actions和hooks
export const {
  setLoading,
  setBatchProcessing,
  setError,
  clearError,
  setProjects,
  setCurrentProject,
  updateProject,
  batchUpdateProjects,
  setFilters,
  clearFilters,
  setPagination,
  setSorting,
  selectProject,
  deselectProject,
  toggleSelectAll,
  clearSelection,
  setStats,
  setViewMode,
  setAutoRefresh,
  setRefreshInterval,
} = screeningSlice.actions;

// 添加缺少的action别名
export const setSortOption = setSorting;
export const setSelectedQuestionnaires = selectProject;
export const clearSelectedQuestionnaires = clearSelection;

export const {
  useGetScreeningProjectsQuery,
  useGetScreeningProjectQuery,
  useReviewProjectMutation,
  useBatchReviewMutation,
  useGetScreeningStatsQuery,
  useExportScreeningDataMutation,
} = screeningApiSlice;

export default screeningSlice.reducer;

// 选择器函数
export const selectAIReport = (state: any, questionnaireId: string) => {
  // 临时实现，返回null或模拟数据
  return null;
};

// 异步thunk函数
export const fetchAIPreReviewReport = (questionnaireId: string) => {
  // 临时实现，返回Promise
  return Promise.resolve(null);
};

export const fetchQuestionnaires = (params?: any) => {
  // 临时实现，返回Promise
  return Promise.resolve([]);
};

export const approveQuestionnaires = (questionnaireIds: string[]) => {
  // 临时实现，返回Promise
  return Promise.resolve({ success: true });
};

export const rejectQuestionnaires = (questionnaireIds: string[]) => {
  // 临时实现，返回Promise
  return Promise.resolve({ success: true });
};

export const batchScreening = (params: { questionnaireIds: string[]; action: string; comments?: string }) => {
  // 临时实现，返回Promise
  return Promise.resolve({ success: true });
};

export const submitScreeningDecision = (params: { 
  questionnaireId: string; 
  decision: 'approved' | 'rejected' | 'need_supplement'; 
  comments?: string; 
  reasons?: string[];
}) => {
  return async (dispatch: any) => {
    try {
      // 使用 questionnaireApi 的 reviewQuestionnaire mutation
      const { questionnaireApi } = await import('../api/questionnaireApi');
      
      const result = await dispatch(
        questionnaireApi.endpoints.reviewQuestionnaire.initiate({
          questionnaireId: params.questionnaireId,
          decision: params.decision,
          comments: params.comments,
          reasons: params.reasons,
        })
      ).unwrap();

      return result;
    } catch (error) {
      throw error;
    }
  };
};

export const fetchStatistics = () => {
  // 临时实现，返回Promise
  return Promise.resolve({
    total_received: 156,
    total_completed: 142,
    pass_rate: 0.78,
    average_processing_time: 3.2,
    industry_distribution: {
      '制造业': 45,
      '金融业': 32,
      '房地产': 28,
      '能源': 25,
      '科技': 26,
    },
    monthly_trend: [
      { month: '2024-01', received: 45, completed: 42, pass_rate: 0.76 },
      { month: '2024-02', received: 52, completed: 48, pass_rate: 0.79 },
      { month: '2024-03', received: 59, completed: 52, pass_rate: 0.81 },
    ],
    reviewer_workload: [
      { reviewer_id: '1', reviewer_name: '张审核员', processed_count: 45, avg_time: 2.8 },
      { reviewer_id: '2', reviewer_name: '李审核员', processed_count: 38, avg_time: 3.1 },
      { reviewer_id: '3', reviewer_name: '王审核员', processed_count: 42, avg_time: 2.9 },
    ],
  });
};

// 其他选择器函数
export const selectScreeningQuestionnaires = (state: any) => {
  // 临时实现，返回模拟问卷数据
  return [
    {
      _id: 'q1',
      id: 'q1',
      status: 'pending',
      submittedAt: '2024-03-15T10:30:00Z',
      companyName: '华能集团',
      projectName: '绿色能源债券',
      score: 85,
      riskLevel: 'low',
    },
    {
      _id: 'q2',
      id: 'q2',
      status: 'approved',
      submittedAt: '2024-03-14T14:20:00Z',
      companyName: '中石化',
      projectName: '清洁能源转型债券',
      score: 92,
      riskLevel: 'low',
    },
    {
      _id: 'q3',
      id: 'q3',
      status: 'rejected',
      submittedAt: '2024-03-13T09:15:00Z',
      companyName: '某制造企业',
      projectName: '环保设备升级债券',
      score: 65,
      riskLevel: 'high',
    },
  ];
};

export const selectSelectedQuestionnaires = (state: any) => {
  // 临时实现，返回空数组
  return [];
};

export const selectFilters = (state: any) => {
  // 临时实现，返回默认筛选器
  return {
    status: [],
    riskLevel: [],
    industry: [],
    scoreRange: [0, 100],
    dateRange: null,
    search: '',
  };
};

export const selectSortOption = (state: any) => {
  // 临时实现，返回默认排序
  return {
    field: 'submittedAt',
    order: 'desc',
  };
};

export const selectPagination = (state: any) => {
  // 临时实现，返回完整分页结构
  return {
    page: 1,
    limit: 20,
    total: 156,
    total_pages: 8,
  };
};

export const selectLoading = (state: any) => {
  // 临时实现，返回loading对象结构
  return {
    list: false,
    batch: false,
    statistics: false,
  };
};

export const selectError = (state: any) => {
  // 临时实现，返回null
  return null;
};

export const selectStats = (state: any) => {
  // 临时实现，返回默认统计
  return {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    averageScore: 0,
    processingTime: 0,
  };
};

export const selectViewMode = (state: any) => {
  // 临时实现，返回table视图
  return 'table';
};

export const selectStatistics = (state: any) => {
  // 临时实现，返回完整统计数据
  return {
    total: 156,
    pending: 14,
    approved: 111,
    rejected: 31,
    averageScore: 78.5,
    processingTime: 3.2,
    total_received: 156,
    total_completed: 142,
    pass_rate: 0.78,
    average_processing_time: 3.2,
    industry_distribution: {
      '制造业': 45,
      '金融业': 32,
      '房地产': 28,
      '能源': 25,
      '科技': 26,
    },
    monthly_trend: [
      { month: '2024-01', received: 45, completed: 42, pass_rate: 0.76 },
      { month: '2024-02', received: 52, completed: 48, pass_rate: 0.79 },
      { month: '2024-03', received: 59, completed: 52, pass_rate: 0.81 },
    ],
    reviewer_workload: [
      { reviewer_id: '1', reviewer_name: '张审核员', processed_count: 45, avg_time: 2.8 },
      { reviewer_id: '2', reviewer_name: '李审核员', processed_count: 38, avg_time: 3.1 },
      { reviewer_id: '3', reviewer_name: '王审核员', processed_count: 42, avg_time: 2.9 },
    ],
  };
};

// TypeScript类型定义
export interface GetScreeningProjectsRequest {
  page?: number;
  limit?: number;
  status?: ScreeningStatus[];
  riskLevel?: RiskLevel[];
  industry?: string[];
  scoreRange?: [number, number];
  dateRange?: [string, string];
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetScreeningProjectsResponse {
  success: boolean;
  data: {
    projects: ScreeningProject[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface GetScreeningProjectResponse {
  success: boolean;
  data: ScreeningProject;
}

export interface ReviewProjectRequest {
  id: string;
  status: 'approved' | 'rejected' | 'revision_required';
  comments?: string;
  score?: number;
}

export interface ReviewProjectResponse {
  success: boolean;
  data: ScreeningProject;
}

export interface BatchReviewRequest {
  projectIds: string[];
  status: 'approved' | 'rejected';
  comments?: string;
}

export interface BatchReviewResponse {
  success: boolean;
  data: {
    processed: number;
    failed: number;
  };
}

export interface ScreeningStatsResponse {
  success: boolean;
  data: ScreeningStats;
}

export interface ExportDataRequest {
  format: 'excel' | 'csv' | 'pdf';
  filters?: Partial<ScreeningFilters>;
  fields?: string[];
}

export interface ExportDataResponse {
  success: boolean;
  data: {
    downloadUrl: string;
    filename: string;
    expiresAt: string;
  };
}